import React, { useState, useEffect, useCallback, useRef } from "react";
import loader from "../../../../assets/Image/Loader2.gif";

const ReusableTable = ({
  columns = [],
  fetchData,
  showAction = false,
  showExport = false,
  showFilter = false,
  enablePagination = true,
  togglePaymentModal,
  onEdit = false,
  onDelete = false,
  onPrint,
  onEnableDisable,
  showEnableDisable = false,
  onAssignDocument,
  showAssignDocument = false,
  searchData,
  refreshKey = 0,
  currentPage: controlledPage,
  onPageChange,
  totalPages: controlledTotalPages,
  showCheckbox = false,
  onBulkDelete,
}) => {
  const [internalPage, setInternalPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const searchTimer = useRef();

  // Determine which page and setPage to use
  const page = controlledPage !== undefined ? controlledPage : internalPage;
  const setPage = onPageChange || setInternalPage;
  const effectiveTotalPages = controlledTotalPages !== undefined ? controlledTotalPages : totalPages;

  const allSelected = data.length > 0 && selectedIds.length === data.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : data.map(item => item.id));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    onBulkDelete(selectedIds);
    setSelectedIds([]);
  };

  const loadData = async (
    pageArg = page,
    search = searchData,
    sortKey = "",
    sortDir = ""
  ) => {
    setLoading(true);
    try {
      const res = await fetchData({
        page: pageArg,
        searchTerm: search,
        sort: sortKey,
        order: sortDir,
      });
      setData(res?.data || []);
      // Only update internal totalPages if not controlled
      if (controlledTotalPages === undefined) {
        setTotalPages(res?.totalPages || 1);
      }
    } catch (err) {
      console.error("Table fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(page, searchTerm, sortConfig.key, sortConfig.direction);
  }, [page, searchTerm, sortConfig.key, sortConfig.direction, refreshKey]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);


  const handleSort = (field) => {
    let direction = "ascending";
    if (sortConfig.key === field && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: field, direction });
  };

  const handleEnableDisable = async (itemId, currentStatus) => {
    if (onEnableDisable) {
      try {
        await onEnableDisable(itemId, currentStatus);
        // Reload data after enable/disable action
        loadData(page, searchTerm, sortConfig.key, sortConfig.direction);
      } catch (error) {
        console.error("Enable/Disable action failed", error);
      }
    }
  };

  const downloadCSV = () => {
    const headers = columns.map((col) => col.header).join(",") + "\n";
    const rows = data.map((row) =>
      columns.map((col) => row[col.field] || "").join(",")
    );
    const csv = headers + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "export.csv";
    link.click();
  };

  const renderPagination = () => {
    const pages = [];
    const maxPages = 5;
    let start = Math.max(1, page - Math.floor(maxPages / 2));
    let end = Math.min(effectiveTotalPages, start + maxPages - 1);
    if (end - start < maxPages) {
      start = Math.max(1, end - maxPages + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${page === i ? "active" : ""}`}
        >
          <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); setPage(i); }}>
            {i}
          </a>
        </li>
      );
    }
    return pages;
  };

  const renderFilterDropdown = () => (
    <div
      className="filter-dropdown"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        backgroundColor: "white",
        border: "1px solid #ddd",
        padding: "10px",
        borderRadius: "5px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        zIndex: 10,
      }}
    >
      <label>
        <input type="checkbox" /> Option A
      </label>
      <br />
      <label>
        <input type="checkbox" /> Option B
      </label>
      <br />
      <button
        className="btn btn-sm btn-primary mt-2"
        onClick={() => setShowDropdown(false)}
      >
        Apply
      </button>
    </div>
  );

  const getSortedIcon = (field) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === "ascending" ? " 🔼" : " 🔽";
  };

  return (
    <div className="data-table-container">
      <div className="d-flex  mb-3">
        <div className="expense-searchcontainerstart d-flex">
          {showCheckbox && selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              style={{ marginRight: '10px', padding: '8px 16px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}
            >
              Delete Selected ({selectedIds.length})
            </button>
          )}
          <div className="search-container">
            <input
              type="text"
              style={{
                width: "300px",
                height: "45px",
                backgroundColor: "#fff",
                border: "1px solid #DBDBDB",
                boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)",
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search"
            />
            <svg
              className="search-container-icon"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5416 19.2497C15.3511 19.2497 19.2499 15.3508 19.2499 10.5413C19.2499 5.73186 15.3511 1.83301 10.5416 1.83301C5.73211 1.83301 1.83325 5.73186 1.83325 10.5413C1.83325 15.3508 5.73211 19.2497 10.5416 19.2497Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.1666 20.1663L18.3333 18.333"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {showExport && (
            <button
              type="button"
              className="btn btn-outline-dark custbtn"
              style={{ borderRadius: "10px", border: "1px solid #f4f4f4" }}
              onClick={downloadCSV}
            >
              <svg
                style={{ marginRight: "5px" }}
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.98975 4.87312L8.90975 2.95312L10.8297 4.87312"
                  stroke="#4A5546"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.91016 10.6353V3.00781"
                  stroke="#4A5546"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 9C3 12.315 5.25 15 9 15C12.75 15 15 12.315 15 9"
                  stroke="#4A5546"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Export To Excel
            </button>
          )}
          {showFilter && (
            <div
              className="filter-button-container"
              style={{ position: "relative" }}
            >
              <button
                className="btn custbtn2"
                type="button"
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "3px" }}
                >
                  <g opacity="0.8">
                    <path
                      d="M19 3H5C3.586 3 2.879 3 2.44 3.412C2.001 3.824 2 4.488 2 5.815V6.505C2 7.542 2 8.061 2.26 8.491C2.52 8.921 2.993 9.189 3.942 9.723L6.855 11.363C7.491 11.721 7.81 11.9 8.038 12.098C8.512 12.509 8.804 12.993 8.936 13.588C9 13.872 9 14.206 9 14.873V17.543C9 18.452 9 18.907 9.252 19.261C9.504 19.616 9.952 19.791 10.846 20.141C12.725 20.875 13.664 21.242 14.332 20.824C15 20.406 15 19.452 15 17.542V14.872C15 14.206 15 13.872 15.064 13.587C15.1896 13.0042 15.5059 12.4798 15.963 12.097C16.19 11.9 16.509 11.721 17.145 11.362L20.058 9.722C21.006 9.189 21.481 8.922 21.74 8.492C22 8.062 22 7.542 22 6.504V5.814C22 4.488 22 3.824 21.56 3.412C21.122 3 20.415 3 19 3Z"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                  </g>
                </svg>
                Filter
              </button>
              {showDropdown && renderFilterDropdown()}
            </div>
          )}
        </div>
      </div>
      <div>
        <div style={{ marginRight: "67%" }}>
          { }
        </div>
      </div>

      <table
        className="data-table custom-table"
        style={{
          borderRadius: "20px 20px 0 0",
          border: "1px solid #EBEAF2",
          borderCollapse: "separate",
          borderSpacing: 0,
          overflow: "hidden",
        }}
      >
        <thead className="table-header">
          <tr>
            {showCheckbox && (
              <th style={{ width: '40px' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
              </th>
            )}
            {columns.map((col, index) => (
              <th key={index} scope="col" onClick={() => handleSort(col.field)}>
                {col.header}
                {sortConfig.key === col.field &&
                  (sortConfig.direction === "ascending" ? (
                    <svg
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "5px" }}
                    >
                      <path d="M0 4L4 0L8 4H0Z" fill="#000000" />
                    </svg>
                  ) : (
                    <svg
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "5px" }}
                    >
                      <path d="M0 0L4 4L8 0H0Z" fill="#000000" />
                    </svg>
                  ))}
              </th>
            ))}
            {showAction && (
              <th scope="col" className="action-column">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody className="table-body">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (showAction ? 2 : 1)}
                style={{
                  textAlign: "center",
                  
                  fontWeight: "600",
                  fontSize: "24px",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.1) -92.86%, #4A5546 71.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                No data found
              </td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr key={item.id || index}>
                {showCheckbox && (
                  <td style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelectOne(item.id)}
                    />
                  </td>
                )}
                {/* <td>{(page - 1) * 10 + index + 1}</td> */}
                {columns.map((col, colIndex) => (
                  <td key={colIndex}>
                    {col.render ? (
                      col.render(item)
                    ) : col.field === "status" ? (
                      <div className={`status ${item[col.field]}`}>
                        {item[col.field]}
                      </div>
                    ) : col.field === "pay_salary" ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          if (item && item.id) {
                            togglePaymentModal(item);
                          } else {
                            console.error("Invalid item data for payment modal");
                          }
                        }}
                      >
                        Pay Salary
                      </button>
                    ) : col.render ? (
                      col.render(item)
                    ) : (
                      item[col.field]
                    )}
                  </td>
                ))}
                {showAction && (
                  <td className="action-column">
                    {/* <button
                      className="action-button"
                      onClick={() => onEdit(item.id, item)}  
                        // onClick={() => onEdit(item.id)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.8067 4.695C14.0667 4.435 14.0667 4.00167 13.8067 3.755L12.2467 2.195C12 1.935 11.5667 1.935 11.3067 2.195L10.08 3.415L12.58 5.915M2 11.5017V14.0017H4.5L11.8733 6.62167L9.37333 4.12167L2 11.5017Z"
                          fill="#000000"
                        />
                      </svg>
                    </button> */}

                    {showAction && onEdit && typeof onEdit === "function" && (
                      <button
                        className="action-button"
                        id="edit-action-button"
                        onClick={() => onEdit(item.id, item)}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.8067 4.695C14.0667 4.435 14.0667 4.00167 13.8067 3.755L12.2467 2.195C12 1.935 11.5667 1.935 11.3067 2.195L10.08 3.415L12.58 5.915M2 11.5017V14.0017H4.5L11.8733 6.62167L9.37333 4.12167L2 11.5017Z"
                            fill="#000000"
                          />
                        </svg>
                      </button>
                    )}
                    {showAction && onDelete && typeof onDelete === "function" && (
                      <button
                        className="action-button"
                         id="delete-action-button"
                        onClick={() => onDelete(item.id)}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.00016 12.6667C4.00016 13.4 4.60016 14 5.3335 14H10.6668C11.4002 14 12.0002 13.4 12.0002 12.6667V6C12.0002 5.26667 11.4002 4.66667 10.6668 4.66667H5.3335C4.60016 4.66667 4.00016 5.26667 4.00016 6V12.6667ZM12.0002 2.66667H10.3335L9.86016 2.19333C9.74016 2.07333 9.56683 2 9.3935 2H6.60683C6.4335 2 6.26016 2.07333 6.14016 2.19333L5.66683 2.66667H4.00016C3.6335 2.66667 3.3335 2.96667 3.3335 3.33333C3.3335 3.7 3.6335 4 4.00016 4H12.0002C12.3668 4 12.6668 3.7 12.6668 3.33333C12.6668 2.96667 12.3668 2.66667 12.0002 2.66667Z"
                            fill="#000000"
                          />
                        </svg>
                      </button>
                    )}
                    {onPrint && typeof onPrint === "function" && (
                      <button
                        className="action-button"
                        onClick={() => onPrint(item)}
                        title="Print"
                        id="print-payslip-button"
                      >🖨️
                        {/* <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13 5H3C2.44772 5 2 5.44772 2 6V11C2 11.5523 2.44772 12 3 12H4V14H12V12H13C13.5523 12 14 11.5523 14 11V6C14 5.44772 13.5523 5 13 5Z"
                            stroke="#000000"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 5V2H4V5"
                            stroke="#000000"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4 9H12V14H4V9Z"
                            stroke="#000000"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg> */}
                      </button>
                    )}
                    {/* Assign Document button - only show if showAssignDocument is true */}
                    {showAssignDocument && onAssignDocument && typeof onAssignDocument === "function" && (
                      <button
                        className="action-button"
                        id="assign-document-button"
                        onClick={() => onAssignDocument(item.id, item)}
                        style={{
                          width: "145px",
                          height: "34px",
                          borderRadius: "10px",
                          backgroundColor: "#4A5546",
                          color: "#fff",
                          padding: "5px 10px",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "14px",
                          marginLeft: "5px",
                        }}
                      >
                        Assign Document
                      </button>
                    )}
                    {/* Enable/Disable button - only show if showEnableDisable is true */}
                    {showEnableDisable && (
                      <button
                        className="action-button"
                        onClick={() =>
                          handleEnableDisable(item.id, item.is_enable)
                        }
                        style={{
                          width: "145px",
                          height: "34px",
                          borderRadius: "10px",
                          backgroundColor: item.is_enable ? "#FF2024" : "#198F51",
                          color: item.is_enable ? "#000" : "#fff",
                          padding: "5px 10px",
                          border: "none",
                          cursor: "pointer",
                          transition: "background-color 0.3s ease",
                          fontSize: "18px",
                          marginLeft: "5px",
                        }}
                      >
                        {item.is_enable ? "Disable Now" : "Enable Now"}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {enablePagination && data.length > 0 && (
        <nav>
          <ul className="pagination">{renderPagination()}</ul>
        </nav>
      )}
    </div>
  );
};

export default ReusableTable;